import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Eye, EyeOff, Lock, Check, X, AlertCircle, 
  Loader2, CheckCircle, UserPlus 
} from 'lucide-react';

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  
  const { verifyInvite, setPassword, isAuthenticated } = useAuth();
  
  const [inviteData, setInviteData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');
  
  const [password, setPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password validation rules
  const passwordRules = [
    { test: (p) => p.length >= 8, label: 'At least 8 characters' },
    { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
    { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
    { test: (p) => /[0-9]/.test(p), label: 'One number' },
    { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;':",.\/<>?]/.test(p), label: 'One special character' },
  ];

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const allRulesPassed = passwordRules.every(rule => rule.test(password));
  const canSubmit = allRulesPassed && passwordsMatch;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin-panel');
    }
  }, [isAuthenticated, navigate]);

  // Verify invite token on mount
  useEffect(() => {
    const verify = async () => {
      if (!inviteToken) {
        setVerifyError('No invite token provided');
        setIsVerifying(false);
        return;
      }

      const result = await verifyInvite(inviteToken);
      
      if (result.success) {
        setInviteData(result);
      } else {
        setVerifyError(result.error);
      }
      
      setIsVerifying(false);
    };

    verify();
  }, [inviteToken, verifyInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    
    setError('');
    setIsSubmitting(true);

    const result = await setPassword(inviteToken, password, confirmPassword);
    
    if (result.success) {
      navigate('/admin-panel');
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#071B3A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying your invite...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired invite
  if (verifyError) {
    return (
      <div className="min-h-screen bg-[#071B3A] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-2xl mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white font-display mb-2">
            Invalid Invite
          </h1>
          <p className="text-slate-400 mb-6">
            {verifyError}
          </p>
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors"
          >
            ← Return to Website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071B3A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-2xl mb-4">
            <UserPlus className="w-8 h-8 text-gold" />
          </div>
          <h1 className="text-2xl font-bold text-white font-display">
            Welcome, {inviteData?.first_name || 'Admin'}!
          </h1>
          <p className="text-slate-400 mt-2">
            You've been invited as <span className="text-gold font-medium">{inviteData?.role}</span>
          </p>
          <p className="text-slate-500 text-sm mt-1">
            {inviteData?.email}
          </p>
        </div>

        {/* Set Password Form */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
          <h2 className="text-lg font-semibold text-white mb-6">
            Create Your Password
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
                  placeholder="Create a strong password"
                  required
                  autoComplete="new-password"
                  data-testid="set-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Rules */}
            <div className="space-y-2">
              {passwordRules.map((rule, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-2 text-sm ${
                    rule.test(password) ? 'text-green-400' : 'text-slate-500'
                  }`}
                >
                  {rule.test(password) ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  {rule.label}
                </div>
              ))}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full pl-12 pr-12 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 transition-all ${
                    confirmPassword && !passwordsMatch 
                      ? 'border-red-500/50' 
                      : confirmPassword && passwordsMatch 
                        ? 'border-green-500/50' 
                        : 'border-white/10'
                  }`}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-red-400 text-sm mt-1">Passwords do not match</p>
              )}
              {confirmPassword && passwordsMatch && (
                <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Passwords match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="w-full py-3 bg-gold text-navy font-semibold rounded-xl hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              data-testid="set-password-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Complete Setup
                </>
              )}
            </button>
          </form>
        </div>

        {/* Security Notice */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Your password must meet all security requirements.
          <br />
          This invite expires on {inviteData?.expires_at ? new Date(inviteData.expires_at).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
          }) : 'soon'}.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
